import { makeAutoObservable, runInAction } from "mobx";
import i18n from "i18next";
import MainStore from "MainStore";

import storeList from "../user_accountListView/store";
import { createuser_account, updateuser_account, getuser_account } from "api/user_accounts"
import { CountryPhone } from "constants/countrieCodes";
import {getroles} from "../../../api/role";
import { getdepartmentsByOrganization } from "api/department";

class NewStore {
  id = 0;
  organization_id = 0;
  username = "";
  email = "";
  full_name = "";
  position = "";
  phone = "";
  selectedCountry: CountryPhone | null = null;
  password = "";
  password_confirm = "";
  has_signing_rights = false;
  is_active = true;
  last_login: string | null = null;
  changePassword = false;
    id_roles: number[];
    Roles = [];

  department_id: number | null = null;
  Departments = [];
  errors: { [key: string]: string } = {};

  constructor() {
    makeAutoObservable(this);
  }

  clearStore() {
    runInAction(() => {
      this.username = "";
      this.email = "";
      this.full_name = "";
      this.position = "";
      this.phone = "";
      this.selectedCountry = null;
      this.password = "";
      this.password_confirm = "";
      this.has_signing_rights = false;
      this.is_active = true;
      this.last_login = null;
      this.organization_id = 0;
      this.department_id = null;
      this.Departments = [];
      this.changePassword = false;
      this.errors = {};
    });
  }

  handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value, type, checked } = event.target;
    const finalValue = type === 'checkbox' ? checked : value;
    (this as any)[name] = finalValue;

    // Clear password fields if changePassword is unchecked
    if (name === 'changePassword' && !checked) {
      this.password = "";
      this.password_confirm = "";
      this.errors.password = "";
      this.errors.password_confirm = "";
    }

    // Validate only the changed field
    this.validateField(name, finalValue);
  }

  handlePhoneChange(value: string, country: CountryPhone) {
    this.phone = value;
    this.selectedCountry = country;
    this.validateField('phone', value);
  }

  validateField(name: string, value: any) {
    // Skip validation for password fields if not changing password
    if (this.id !== 0 && !this.changePassword && (name === 'password' || name === 'password_confirm')) {
      return;
    }

    // Required field validation
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      this.errors[name] = i18n.t("message:error.required");
      return;
    }

    // Email validation
    if (name === 'email') {
      const emailRegex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!emailRegex.test(value)) {
        this.errors[name] = i18n.t("message:error.emailAddress");
        return;
      }
    }

    // Phone validation - check that we have country code + at least some digits
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      // Phone should have country code + at least 5 digits for local number
      if (digitsOnly.length < 6) {
        this.errors[name] = i18n.t("message:error.phoneInvalid") || "Invalid phone number";
        return;
      }
    }

    // Clear error if validation passes
    this.errors[name] = "";
  }

  async onSaveClick(onSaved: (id: number) => void) {
    // Collect form data
    const data: any = {
      id: this.id,
      organization_id: storeList.idMain,
      username: this.username.trim(),
      email: this.email.trim(),
      full_name: this.full_name.trim(),
      position: this.position.trim(),
      phone: this.phone,
      has_signing_rights: this.has_signing_rights,
      is_active: this.is_active,
      department_id: this.department_id,
        roles: this.id_roles
    };

    // Add password only if creating new user or changing password
    if (this.id === 0 || this.changePassword) {
      data.password = this.password;
      data.password_confirm = this.password_confirm;
    }

    // Validation
    let isValid = true;
    this.errors = {};

    // Required fields
    if (!data.username) {
      this.errors.username = i18n.t("message:error.required");
      isValid = false;
    }

    // Email validation
    if (!data.email) {
      this.errors.email = i18n.t("message:error.required");
      isValid = false;
    } else {
      const emailRegex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!emailRegex.test(data.email)) {
        this.errors.email = i18n.t("message:error.emailAddress");
        isValid = false;
      }
    }

    if (!data.full_name) {
      this.errors.full_name = i18n.t("message:error.required");
      isValid = false;
    }

    if (!data.position) {
      this.errors.position = i18n.t("message:error.required");
      isValid = false;
    }

    // Phone validation
    if (!data.phone) {
      this.errors.phone = i18n.t("message:error.required");
      isValid = false;
    } else {
      const digitsOnly = data.phone.replace(/\D/g, '');
      if (digitsOnly.length < 6) {
        this.errors.phone = i18n.t("message:error.phoneInvalid") || "Invalid phone number";
        isValid = false;
      }
    }

    if (!isValid) {
      MainStore.openErrorDialog(i18n.t("message:error.alertMessageAlert"));
      return;
    }

    try {
      MainStore.changeLoader(true);
      let response;
      if (this.id === 0) {
        response = await createuser_account(data);
      } else {
        response = await updateuser_account(data);
      }

      if (response.status === 201 || response.status === 200) {
        onSaved(response.data?.id || response.status);
        MainStore.setSnackbar(
          this.id === 0
            ? i18n.t("message:snackbar.successSave")
            : i18n.t("message:snackbar.successEdit"),
          "success"
        );
      } else {
        throw new Error("Save failed");
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  }

  async doLoad(id: number) {
      await this.loadRoles();
      await this.loadDepartments();
    this.id = id;
    if (id === 0 || id === null) {
      this.is_active = true;
      return;
    }
    this.loaduser_account(id);
  }

  loaduser_account = async (id: number) => {
    try {
      MainStore.changeLoader(true);
      const response = await getuser_account(id);
      if ((response.status === 201 || response.status === 200) && response?.data) {
        runInAction(() => {
          const data = response.data;
          this.id = data.id;
          this.organization_id = data.organization_id;
          this.username = data.username || "";
          this.email = data.email || "";
          this.full_name = data.full_name || "";
          this.position = data.position || "";
          this.phone = data.phone || "";
          this.has_signing_rights = data.has_signing_rights;
          this.is_active = data.is_active;
          this.last_login = data.last_login;
          this.password = "";
          this.password_confirm = "";
          this.changePassword = false;
          this.id_roles = data.roles;
          this.department_id = data.department_id ?? null;
        });
      } else {
        throw new Error("Failed to load user account");
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  }

    changeRoles(ids: number[]) {
        this.id_roles = ids;
    }

    loadRoles = async () => {
        try {
            MainStore.changeLoader(true);
            const response = await getroles();
            if ((response.status === 201 || response.status === 200) && response?.data !== null) {
                runInAction(() => {
                    this.Roles = response.data
                });
            } else {
                throw new Error();
            }
        } catch (err) {
            MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
        } finally {
            MainStore.changeLoader(false);
        }
    }

    loadDepartments = async () => {
        try {
            const orgId = storeList.idMain;
            if (!orgId) return;
            const response = await getdepartmentsByOrganization(orgId);
            if ((response.status === 201 || response.status === 200) && response?.data !== null) {
                runInAction(() => {
                    this.Departments = response.data;
                });
            }
        } catch (err) {
            // не критично — просто не показываем отделы
        }
    }
}

export default new NewStore();